import { useGetNonProfitById } from '@/actions/nonprofit.actions';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import {
  InKindDonationDeliveryMethod,
  InKindDonationStatus,
} from '../../constants/inKindDonation.constant';
import SingleImageSelector from '../FileSelectors/SingleImageSelector';
import Button from '../next-ui/Button';
import { ToggleButton } from '../next-ui/ToggleButton';
import Typography from '../next-ui/Typography';
import { confirmInKindDelivery } from '@/actions/campaign.actions';

const schema = yup.object().shape({
  deliveryMode: yup
    .string()
    .oneOf([
      InKindDonationDeliveryMethod.IN_PERSON,
      InKindDonationDeliveryMethod.ONLINE_OR_POSTAL,
    ])
    .required('Delivery mode is required'),
  image: yup.mixed().when('deliveryMode', {
    is: InKindDonationDeliveryMethod.ONLINE_OR_POSTAL,
    then: yup.mixed().test({
      name: 'fileValidator',
      message: 'Please upload a valid image file',
      test: (value) => value && value.type.startsWith('image/'),
    }),
    otherwise: yup.mixed().notRequired().nullable(),
  }),
});

function InKindDeliveryConfirmForm(props) {
  const { post, donation, onSubmit, updateNonProfit } = props;
  const campaign = post.sourcePostData;

  const [donatedGoods, setDonatedGoods] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  const { data: nonProfit, isLoading } = useGetNonProfitById(
    donation?.nonProfitId,
  );

  const isStatusPledged = donation.status === InKindDonationStatus.PLEDGED;

  const formMethods = useForm({
    mode: 'onChange',
    defaultValues: {
      deliveryMode: InKindDonationDeliveryMethod.IN_PERSON,
      image: null,
    },
    resolver: yupResolver(schema),
  });

  const deliveryMode = formMethods.watch('deliveryMode');

  useEffect(() => {
    if (nonProfit) {
      updateNonProfit(nonProfit);
      const { goods } = donation;
      const donatedGoodsText = goods
        .map((good) => {
          const { id, quantity } = good;
          const givingLevel = post.renderProps.inKindDonations.find(
            (item) => item.id === id,
          );
          if (givingLevel) {
            return `${quantity} ${givingLevel.title}`;
          }
          return null;
        })
        .filter((text) => !!text)
        .join(', ');

      setDonatedGoods(donatedGoodsText);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nonProfit]);

  const handleDeliveryMode = (mode) => {
    formMethods.setValue('deliveryMode', mode, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const handleFormSubmit = async () => {
    setSaving(true);
    const { image } = formMethods.getValues();
    const { data, ok } = await confirmInKindDelivery(
      donation.id,
      deliveryMode,
      deliveryMode === InKindDonationDeliveryMethod.ONLINE_OR_POSTAL
        ? image
        : null,
    );
    setSaving(false);
    if (ok) {
      onSubmit('confirmed', data);
    } else {
      onSubmit('close');
    }
  };

  return (
    <form
      className="w-full"
      onSubmit={formMethods.handleSubmit(handleFormSubmit)}>
      {!isStatusPledged ? (
        <Typography variant="body1">
          You have already sent your in-kind donation. Thank you for your
          contribution.
        </Typography>
      ) : (
        <div className="flex flex-col items-start gap-4">
          <Typography variant="body1">
            Have you sent following in-kind donation items that you pledged to
            the non-profit?
          </Typography>

          <div className="grid grid-cols-2 items-center gap-x-1 gap-y-3">
            <Typography variant="h5" className="font-medium">
              Items:
            </Typography>
            <Typography variant="body1">{donatedGoods}</Typography>

            <Typography variant="h5" className="font-medium">
              Delivery Address:
            </Typography>
            <Typography variant="body1">{campaign.address}</Typography>

            <Typography variant="h5" className="font-medium">
              Non-Profit:
            </Typography>
            <Typography variant="body1">
              {nonProfit ? nonProfit.name : ''}
            </Typography>

            <Typography variant="h5" className="font-medium">
              Campaign Name:
            </Typography>
            <Typography variant="body1">{campaign.title}</Typography>
          </div>

          <div className="flex items-start justify-start gap-3">
            <ToggleButton
              label="I have personally delivered the items"
              selected={deliveryMode === InKindDonationDeliveryMethod.IN_PERSON}
              onToggle={() =>
                handleDeliveryMode(InKindDonationDeliveryMethod.IN_PERSON)
              }
            />

            <ToggleButton
              label="I have mailed the items via Online or Postal service"
              selected={
                deliveryMode === InKindDonationDeliveryMethod.ONLINE_OR_POSTAL
              }
              onToggle={() =>
                handleDeliveryMode(
                  InKindDonationDeliveryMethod.ONLINE_OR_POSTAL,
                )
              }
            />
          </div>

          {deliveryMode === InKindDonationDeliveryMethod.ONLINE_OR_POSTAL && (
            <Controller
              name="image"
              control={formMethods.control}
              render={({ field }) => (
                <SingleImageSelector
                  {...field}
                  autoOpen={false}
                  placeholder="Select or drop the online receipt for your in-kind donation- here"
                  error={formMethods.formState.errors.image?.message}
                />
              )}
            />
          )}
        </div>
      )}

      {isStatusPledged ? (
        <div className="flex w-full items-center justify-end gap-4 pt-3">
          <Button
            variant="outlined"
            color="primary"
            onClick={() => onSubmit('confirm-later')}>
            Confirm Later
          </Button>
          <Button variant="filled" type="submit">
            Confirm
          </Button>
        </div>
      ) : (
        <div className="flex w-full items-center justify-end gap-4 pt-3">
          <Button variant="filled" onClick={() => onSubmit('close')}>
            Close
          </Button>
        </div>
      )}
    </form>
  );
}

export default InKindDeliveryConfirmForm;
